<template>
  <div id="app">
    <component
      v-if="layout"
      :is="layout"
    >
      <RouterView />
    </component>
  </div>
</template>

<script>
import DefaultLayout from '@/layouts/DefaultLayout';
import Company from '@/orm/models/Company/Company';
import CompanySettings from '@/orm/models/Company/Settings';
import Vacancy from '@/orm/models/Vacancy/Vacancy';
import Candidate from '@/orm/models/Candidate/Candidate';
import CandidateLog from '@/orm/models/Candidate/CandidateLog';
import Interview from '@/orm/models/Interview/Interview';
import InterviewLog from '@/orm/models/Interview/InterviewLog';
import OfficeInterview from '@/orm/models/OfficeInterview/OfficeInterview';
import OfficeInterviewLog from '@/orm/models/OfficeInterview/OfficeInterviewLog';
import Sentry from '@/sentry';
import api from '@/api';
import User from '@/orm/models/User/User';
import { appLayouts } from '@/enums';
import IntegrationsLayout from '@/layouts/IntegrationsLayout';
import InviteLayout from '@/layouts/InviteLayout';
import Settings from '@/orm/models/User/Settings';
import Address from '@/orm/models/Address/Address';
import Notification from '@/orm/models/Notification/Notification';
import SystemNotification from '@/orm/models/SystemNotification/SystemNotification';
import { markSystemNotificationJustReceived } from '@/compositions/useSystemNotifications';
import { kanbanMutations } from '@/store/modules/kanban';
import Locale from '@/locale';
import Account from '@/orm/models/Account/Account';
import VacancySettings from '@/orm/models/Vacancy/VacancySettings';
import NotificationTemplate from '@/orm/models/Company/NotificationTemplate/NotificationTemplate';

export default {
  name: 'App',
  components: {
    DefaultLayout,
    IntegrationsLayout,
    InviteLayout,
  },
  data() {
    return {
      layout: null,
    };
  },
  metaInfo() {
    return {
      htmlAttrs: {
        lang: this.$i18n.locale,
      },
    };
  },
  computed: {
    user() {
      return User.getAuthUser();
    },
  },
  watch: {
    user(newValue, oldValue) {
      Sentry.setUser({
        id: newValue?.id,
        email: newValue?.email,
        username: newValue ? `${newValue.firstName} ${newValue.lastName}` : null,
      });

      if (!newValue && oldValue) {
        this.$echo.disconnect();
        this.wsLeave(oldValue);
      }
      if (newValue && !oldValue) {
        Locale.change(newValue.settings.locale);

        this.$echo.disconnect();
        this.$echo.connect();
        this.wsListen(newValue);

        if (this.$route.name !== 'notifications') {
          this.loadSystemNotifications();
        }
      }
    },
    $route: {
      deep: true,
      handler(to) {
        if (to.path === document.location.pathname) {
          this.layout = to.meta?.layout || appLayouts.DEFAULT;
        }
      },
    },
  },
  async beforeCreate() {
    try {
      await api.get('/users/csrf-cookie');
    } catch (e) {
      console.error(e);
    }
  },
  async beforeMount() {
    if (this.user?.createdUid) {
      await this.redirectToLogout();
    }
  },
  created() {
    if (this.user) {
      this.wsListen(this.user);

      if (this.$route.name !== 'notifications') {
        this.loadSystemNotifications();
      }
    }

    this.setCookiesData();
  },
  methods: {
    async redirectToLogout() {
      if (this.$route.name !== 'change-temp-password'
        && this.$route.name !== 'logout'
        && this.$route.name !== 'login'
      ) {
        await this.$router.push({ name: 'logout' });
      }
    },
    wsListen(user) {
      this.$echo.private(`user.${user.id}`)
        .notification((data) => {
          Notification.insert({ data });
        })
        .listen('.system_notification.sent', (data) => {
          const notification = data?.notification || data?.data || data;

          SystemNotification.handleSent(data);

          markSystemNotificationJustReceived(notification?.id);
        })
        .listen('.system_notification.unread_counter_updated', (data) => {
          SystemNotification.handleUnreadCounterUpdated(data);
        })
        .listen('.user.settings.updated', (data) => Settings.insert({ data }))
        .listen('.user.updated', (data) => User.insert({ data }))
        .listen('.company.account.updated', (data) => {
          Account.insert({ data });
        });
      this.$echo.private(`company.${user.companyId}`)
        .listen('.company.updated', (data) => Company.insert({ data }))
        .listen('.company.settings.updated', (data) => CompanySettings.insert({ data }))
        .listen('.vacancy.created', (data) => Vacancy.insert({ data }))
        .listen('.vacancy.updated', (data) => Vacancy.insert({ data }))
        .listen('.vacancy.settings.updated', (data) => VacancySettings.insert({ data }))
        .listen('.vacancy.status.changed', (data) => Vacancy.insert({ data }))
        .listen('.vacancy.destroyed', (data) => {
          if (!String(this.$router.currentRoute.path).startsWith(`/vacancy/${data.id}`)) {
            Vacancy.delete(data.id);
          }
        })
        .listen('.candidate.created', (data) => Candidate.insert({ data }))
        .listen('.candidate.updated', (data) => Candidate.insert({ data }))
        .listen('.candidate.destroyed', (data) => {
          const route = this.$router.currentRoute;
          if (route.name === 'candidate' && String(route.params.candidateId) === String(data.id)) {
            return;
          }
          Candidate.delete(data.id);
        })

        .listen('.candidate.logs.created', (data) => CandidateLog.insert({ data }))
        .listen('.candidate.logs.destroyed', (data) => CandidateLog.delete(data.id))

        .listen('.candidate.interview.created', (data) => Interview.insert({ data }))
        .listen('.candidate.interview.updated', (data) => Interview.insert({ data }))
        .listen('.candidate.interview.destroyed', (data) => Interview.delete(data.id))

        .listen('.candidate.interview.logs.created', (data) => InterviewLog.insert({ data }))
        .listen('.candidate.interview.logs.destroyed', (data) => InterviewLog.delete(data.id))

        .listen('.candidate.office_interview.created', (data) => OfficeInterview.insert({ data }))
        .listen('.candidate.office_interview.updated', (data) => OfficeInterview.insert({ data }))
        .listen('.candidate.office_interview.destroyed', (data) => OfficeInterview.delete(data.id))

        .listen('.candidate.office_interview.logs.created', (data) => OfficeInterviewLog.insert({ data }))
        .listen('.candidate.office_interview.logs.destroyed', (data) => OfficeInterviewLog.delete(data.id))
        .listen('.addresses.attached', (data) => Address.insert({ data }))
        .listen('.addresses.updated', (data) => Address.insert({ data }))
        .listen('.addresses.destroyed', (data) => Address.delete(data.id))
        .listen(
          '.candidate_status.created',
          (data) => this.$store.commit(`kanban/${kanbanMutations.CREATE_STAGE}`, data),
        )
        .listen(
          '.candidate_status.updated',
          (data) => this.$store.commit(`kanban/${kanbanMutations.UPDATE_STAGE}`, data),
        )
        .listen(
          '.candidate_status.destroyed',
          (data) => this.$store.commit(`kanban/${kanbanMutations.DELETE_STAGE}`, data),
        )
        .listen('.company.account.quota.updated', (data) => {
          Account.update({ where: data.accountId, data: { quota: data } });
        })
        .listen(
          '.company_notification_template.updated',
          (data) => NotificationTemplate.insert({ data }),
        );
    },
    wsLeave(user) {
      this.$echo.leave(`company.${user.company.id}`);
      this.$echo.leave(`user.${user.id}`);
    },
    async loadSystemNotifications() {
      await SystemNotification.fetchUnreadCount();
    },
    setCookiesData() {
      const urlParams = new URLSearchParams(window.location.search);
      const referralCode = urlParams.get('referralCode');
      if (referralCode) {
        document.cookie = `referralCode=${referralCode}`;
      }
    },
  },
};
</script>
